// src/app/auth/authAdapter.ts
//
// Single source of truth for Super Admin auth state. Wraps
// providers/localAuthProvider.ts (a real mobile+password provider
// backed by the representative-auth backend — see its own doc
// comment for why it doesn't depend on @vanialabs/core-auth) and
// exposes nothing more than the five primitives below.
//
// Nothing outside this file should import providers/localAuthProvider.ts
// directly, and no component should read session state any other way
// — components consume auth exclusively through useAuth.ts, which is
// itself just these five functions wired into React via
// useSyncExternalStore. There is no second copy of session logic
// anywhere in the app.
//
// This is also the single swap point for a real backend Auth Service
// later: replace the bodies below, keep the same five exports, and
// nothing else in the app needs to change.
import * as localAuthProvider from './providers/localAuthProvider';
import type { AuthSession } from './providers/localAuthProvider';
import type { AuthUser } from './types';

export type { AuthSession, AuthUser };

export interface LoginResult {
  ok: boolean;
  error?: string;
}

/** Attempts to sign in with the given mobile/password. See localAuthProvider.ts for the real backend call. */
export async function login(mobile: string, password: string): Promise<LoginResult> {
  const result = await localAuthProvider.login(mobile, password);
  // exactOptionalPropertyTypes: `error` must be present-and-string or
  // absent entirely — never present-and-undefined — so this is a
  // conditional spread rather than `error: result.error`.
  return result.error === undefined
    ? { ok: result.ok }
    : { ok: result.ok, error: result.error };
}

/** Clears the session. Does not navigate — that's the caller's job (see UserArea.tsx). */
export function logout(): void {
  localAuthProvider.logout();
}

export function getSession(): AuthSession | null {
  return localAuthProvider.getSession();
}

export function isAuthenticated(): boolean {
  return localAuthProvider.isAuthenticated();
}

/**
 * Subscribes to session changes (login/logout). Returns an
 * unsubscribe function. This is the reactive-update mechanism
 * useAuth.ts is built on — nothing else in the app calls this
 * directly.
 */
export function subscribe(listener: () => void): () => void {
  return localAuthProvider.subscribe(listener);
}
